import { api } from '@verifime/api-definition';
import { TOrganisationCodeWorkflowType } from '@verifime/components';
import { useEffect, useState } from 'react';
import { TNotificationHandler } from '../../../../types';

export interface UseOrganisationCodeWorkflowResult {
  selectedCode: string;
  setSelectedCode: (code: string) => void;
  isLoading: boolean;
  organisationCodeWorkflow: Map<string, TOrganisationCodeWorkflowType>;
  handleWorkflowSwitch: (workflow: TOrganisationCodeWorkflowType) => Promise<void>;
  organisationCodes: string[];
  selectedWorkflow: TOrganisationCodeWorkflowType;
  disabledWorkflows: Record<TOrganisationCodeWorkflowType, boolean>;
}

export function useOrganisationCodeWorkflow(
  organisationId: string,
  successNotificationHandler: TNotificationHandler = console.log,
  errorNotificationHandler: TNotificationHandler = console.error,
): UseOrganisationCodeWorkflowResult {
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [organisationCodeWorkflow, setOrganisationCodeWorkflow] = useState<
    Map<string, TOrganisationCodeWorkflowType>
  >(new Map());

  // Fetch organisation codes and their workflows
  useEffect(() => {
    setIsLoading(true);
    api
      .getV1organisationOrganisationIdcode({
        params: { organisationId },
      })
      .then((data) => {
        const mapping = new Map<string, TOrganisationCodeWorkflowType>();
        data.forEach((item) => {
          mapping.set(item.code, item.onboardingWorkflowType);
        });
        setOrganisationCodeWorkflow(mapping);
        // Set the first code as default selection
        if (mapping.size > 0) {
          setSelectedCode(Array.from(mapping.keys())[0]);
        }
      })
      .catch((err) => {
        errorNotificationHandler(err instanceof Error ? err.message : 'An unknown error occurred');
        console.error('Error fetching organisation codes:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [organisationId, errorNotificationHandler]);

  // Handle workflow switching
  const handleWorkflowSwitch = async (workflow: TOrganisationCodeWorkflowType) => {
    const currentWorkflow = organisationCodeWorkflow.get(selectedCode);

    // Don't do anything if selecting the same workflow
    if (workflow === currentWorkflow) {
      return;
    }

    if (!selectedCode) {
      errorNotificationHandler('No organisation code selected.');
      return;
    }

    try {
      await api.putV1organisationcodeOrganisationCodeworkflow(
        { workflowType: workflow },
        { params: { organisationCode: selectedCode } },
      );
      setOrganisationCodeWorkflow((prev) => new Map(prev).set(selectedCode, workflow));
      successNotificationHandler('Workflow updated successfully');
    } catch (err) {
      errorNotificationHandler(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const selectedWorkflow = organisationCodeWorkflow.get(selectedCode) || 'DEFAULT';
  const organisationCodes = Array.from(organisationCodeWorkflow.keys());

  // Calculate which workflow buttons should be disabled based on current workflow
  // Invalid transitions: INDIVIDUAL_ONLY <-> ORGANISATION_ONLY (must go through DEFAULT)
  const disabledWorkflows: Record<TOrganisationCodeWorkflowType, boolean> = {
    DEFAULT: false,
    INDIVIDUAL_ONLY: selectedWorkflow === 'ORGANISATION_ONLY',
    ORGANISATION_ONLY: selectedWorkflow === 'INDIVIDUAL_ONLY',
  };

  return {
    selectedCode,
    setSelectedCode,
    isLoading,
    organisationCodeWorkflow,
    handleWorkflowSwitch,
    organisationCodes,
    selectedWorkflow,
    disabledWorkflows,
  };
}
